import { Megaphone, MoreHorizontal, Plus, Search, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { useTour } from "@/components/layout/tour";
import { DeleteCampaignDialog } from "@/features/campaign-delete";
import { CampaignFormDialog } from "@/features/campaign-form";
import { NewLeadLine } from "@/features/new-lead-line";
import { useAuth } from "@/lib/auth";
import { dialModeText, purposeText, statusText } from "@/lib/campaign";
import { useCampaigns } from "@/lib/queries";
import type { Tour } from "@/lib/tours";
import { atLeast, type Campaign } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";

/** The screen's product tour (`components/layout/tour.tsx`): each step points at the element
 *  carrying its `data-tour` name below. The steps for a row need a campaign in the list, and the
 *  tour leaves them out when there is none. */
const CAMPAIGNS_TOUR: Tour = {
  id: "campaigns",
  version: 1,
  steps: [
    {
      target: "campaigns-list",
      title: "Your campaigns",
      body: "One row per firm whose leads we work. Status says whether a campaign is live, and Dialer says whether the dialer is calling its lists.",
      side: "top",
    },
    {
      target: "campaigns-open",
      title: "Open a campaign",
      body: "Click a name to set how its calls are routed, who takes them, which outcomes reps can pick and how its leads are dialed.",
    },
    {
      target: "campaigns-search",
      title: "Find a campaign",
      body: "Type part of a name or a slug. The list narrows as you type.",
    },
    {
      target: "campaigns-new",
      title: "Add a campaign",
      body: "Create one per firm. Give it a name first, then route its numbers to it and set its lines on its own page.",
      minimum: "admin",
      align: "end",
    },
    {
      target: "campaigns-actions",
      title: "Edit or delete",
      body: "Change a campaign's details here. A campaign cannot be deleted while numbers, triggers or outcomes still send it calls or leads; the delete names each one.",
      minimum: "admin",
      side: "left",
    },
  ],
};

/**
 * Campaigns: the firms whose leads this organisation works. Each one's name opens its page, where
 * its lines, staffing, dispositions, dialing and lists are set (`routes/campaign.tsx`); this list
 * says at a glance which campaigns the dialer is working.
 */
export function CampaignsPage() {
  const { user: actor } = useAuth();
  const navigate = useNavigate();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";

  const [search, setSearch] = React.useState("");
  const debounced = useDebounced(search, 250);
  const { data, isPending, error, refetch } = useCampaigns({ q: debounced || undefined });

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Campaign | undefined>();
  const [deleting, setDeleting] = React.useState<Campaign | undefined>();

  useTour(CAMPAIGNS_TOUR, { ready: !isPending && !error });

  return (
    <Page>
      <PageHeader
        title="Campaigns"
        description="The firms whose leads you work. Open one to set how its calls are routed, who takes them, and how its leads are dialed."
        action={
          canManage && (
            <Button
              variant="default"
              data-tour="campaigns-new"
              onClick={() => {
                setEditing(undefined);
                setFormOpen(true);
              }}
            >
              <Plus />
              New campaign
            </Button>
          )
        }
      />

      {canManage && actor?.tenant_id != null && <NewLeadLine tenantId={actor.tenant_id} />}

      <Card className="overflow-hidden" data-tour="campaigns-list">
        <Toolbar>
          <Input
            data-tour="campaigns-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or slug"
            leading={<Search />}
            className="w-56"
            aria-label="Search campaigns"
          />
          <span className="ml-auto text-xs text-text-muted">
            {data ? `${data.length} campaign${data.length === 1 ? "" : "s"}` : null}
          </span>
        </Toolbar>

        {isPending ? (
          <TableSkeleton cols={8} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load campaigns"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title={debounced ? "No campaigns match" : "No campaigns yet"}
            description={
              debounced
                ? "Try a different name or slug."
                : "Create one per firm, then route its numbers to it."
            }
            action={
              canManage && !debounced ? (
                <Button variant="default" onClick={() => setFormOpen(true)}>
                  <Plus />
                  New campaign
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Dialer</TableHead>
                <TableHead>Numbers</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c, row) => {
                const status = statusText(c.status);
                // The tour points at the first row's name and menu.
                const first = row === 0;
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <Link
                          to={`/campaigns/${c.id}`}
                          className="font-medium text-text underline-offset-4 hover:underline"
                          data-tour={first ? "campaigns-open" : undefined}
                        >
                          {c.name}
                        </Link>
                        {!c.enabled && (
                          <Badge tone="warning" size="sm">
                            Disabled
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <MonoCell>
                      <span className="inline-flex items-center gap-1">
                        {c.slug}
                        <CopyButton value={c.slug} />
                      </span>
                    </MonoCell>
                    <TableCell className="text-text-muted">
                      {c.state ?? "—"}
                      <span className="ml-1.5 text-xs text-text-subtle">{c.language.toUpperCase()}</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <Badge tone={status.tone} size="sm">
                          {status.label}
                        </Badge>
                      </span>
                    </TableCell>
                    <TableCell className="text-text-muted">
                      {purposeText(c.purpose).label}
                    </TableCell>
                    <TableCell className={c.dial_mode === "off" ? "text-text-subtle" : "text-text"}>
                      {dialModeText(c.dial_mode).label}
                    </TableCell>
                    <TableCell>
                      <Badge tone={c.did_count > 0 ? "neutral" : "outline"}>{c.did_count}</Badge>
                    </TableCell>
                    {isSuperadmin && (
                      <TableCell className="text-xs text-text-muted">{c.tenant_name ?? "—"}</TableCell>
                    )}
                    <TableCell>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Actions for ${c.name}`}
                              data-tour={first ? "campaigns-actions" : undefined}
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditing(c);
                                setFormOpen(true);
                              }}
                            >
                              <Settings2 />
                              Edit details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem destructive onSelect={() => setDeleting(c)}>
                              <Trash2 />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <CampaignFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        campaign={editing}
        onCreated={(created) => navigate(`/campaigns/${created.id}`)}
      />

      <DeleteCampaignDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        campaign={deleting}
        onDeleted={() => setDeleting(undefined)}
      />
    </Page>
  );
}
